/**
 * Preset project colors. A project stores a color *key* (or null); the UI maps
 * the key to Tailwind classes so projects read consistently in light/dark and
 * survive a theme/brand swap. Unknown/null keys fall back to a neutral gray.
 */
export interface ProjectColor {
  key: string;
  label: string;
  /** Small color dot (swatch + chip leading dot). */
  dot: string;
  /** Chip background/text/border. */
  chip: string;
}

export const PROJECT_COLORS: ProjectColor[] = [
  {
    key: 'gray',
    label: 'Gray',
    dot: 'bg-slate-400',
    chip: 'bg-slate-100 text-slate-700 dark:bg-slate-800/60 dark:text-slate-300',
  },
  {
    key: 'red',
    label: 'Red',
    dot: 'bg-red-500',
    chip: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400',
  },
  {
    key: 'orange',
    label: 'Orange',
    dot: 'bg-orange-500',
    chip: 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400',
  },
  {
    key: 'amber',
    label: 'Amber',
    dot: 'bg-amber-500',
    chip: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400',
  },
  {
    key: 'green',
    label: 'Green',
    dot: 'bg-green-500',
    chip: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400',
  },
  {
    key: 'teal',
    label: 'Teal',
    dot: 'bg-teal-500',
    chip: 'bg-teal-100 text-teal-700 dark:bg-teal-900/30 dark:text-teal-400',
  },
  {
    key: 'blue',
    label: 'Blue',
    dot: 'bg-blue-500',
    chip: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400',
  },
  {
    key: 'indigo',
    label: 'Indigo',
    dot: 'bg-indigo-500',
    chip: 'bg-indigo-100 text-indigo-700 dark:bg-indigo-900/30 dark:text-indigo-400',
  },
  {
    key: 'purple',
    label: 'Purple',
    dot: 'bg-purple-500',
    chip: 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400',
  },
  {
    key: 'pink',
    label: 'Pink',
    dot: 'bg-pink-500',
    chip: 'bg-pink-100 text-pink-700 dark:bg-pink-900/30 dark:text-pink-400',
  },
];

const DEFAULT_COLOR = PROJECT_COLORS[0];

/** Resolve a stored color key (or null) to its palette entry, falling back to gray. */
export function resolveProjectColor(key: string | null | undefined): ProjectColor {
  return PROJECT_COLORS.find((c) => c.key === key) ?? DEFAULT_COLOR;
}
